import React, { useState } from 'react'

/**
 * MetricCalculationAudit:
 * Desglose auditable y transparente del cálculo matemático y fisiológico de cada métrica.
 * Se muestra oculto por defecto para mantener la UI limpia y sin sobrecarga visual,
 * pero permite a médicos, auditores o al usuario verificar en 1 clic la exactitud matemática.
 */
export function MetricCalculationAudit({
  title,
  formula,
  variables = [],
  stepByStep,
  reference,
  style = {}
}) {
  const [open, setOpen] = useState(false)

  return (
    <div style={{ marginTop: 10, ...style }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none',
          border: 'none',
          padding: '4px 0',
          color: open ? 'var(--text)' : 'var(--text-faint)',
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          transition: 'color 0.15s ease',
          userSelect: 'none'
        }}
        title="Ver o validar la fórmula matemática y variables usadas en esta sesión"
      >
        <span style={{
          display: 'inline-block',
          fontSize: 8.5,
          transition: 'transform 0.2s ease',
          transform: open ? 'rotate(90deg)' : 'none'
        }}>
          ▶
        </span>
        <span style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>
          {open ? `Ocultar auditoría de cálculo (${title})` : `Validar cálculo matemático (${title})`}
        </span>
      </button>

      {open && (
        <div style={{
          marginTop: 8,
          background: 'var(--field-bg)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: '12px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          animation: 'fadeIn 0.2s ease',
          fontSize: 12,
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.02)'
        }}>
          {/* 1. Fórmula Matemática Estándar */}
          {formula && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Fórmula Clínica Estándar
              </div>
              <div style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-soft)',
                borderRadius: 8,
                padding: '7px 10px',
                marginTop: 4,
                fontFamily: 'var(--mono, "JetBrains Mono", monospace)',
                fontSize: 11.5,
                color: '#007aff',
                fontWeight: 600,
                wordBreak: 'break-all'
              }}>
                {formula}
              </div>
            </div>
          )}

          {/* 2. Variables Reales de la Sesión */}
          {variables && variables.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Variables Reales Usadas en Esta Sesión
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 6, marginTop: 4 }}>
                {variables.map((v, i) => (
                  <div key={i} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-soft)', borderRadius: 7, padding: '5px 8px' }}>
                    <div style={{ fontSize: 9.5, color: 'var(--text-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {v.label}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                      {v.value ?? '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 3. Paso a Paso de Resolución */}
          {stepByStep && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Procedimiento Paso a Paso
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: 11.5, lineHeight: 1.45, marginTop: 4 }}>
                {stepByStep}
              </div>
            </div>
          )}

          {/* 4. Norma o Referencia Médica Oficial */}
          {reference && (
            <div style={{ fontSize: 10.5, color: 'var(--text-faint)', borderTop: '1px solid var(--border-soft)', paddingTop: 6 }}>
              <strong>Norma Médica:</strong> {reference}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
export default MetricCalculationAudit
